import React, { useCallback, useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { SlideOut } from '@interstate/components/SlideOut';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { SplitButton } from '@interstate/components/SplitButton';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { useGetDrawerWidth } from '../dealActivity/activities/hooks/useGetDrawerWidth';
import { PrintProposalSelector } from './PrintProposalSelector';
import { PrintProposalPreview } from './PrintProposalPreview';
import { PrintScenarioSelector } from './PrintScenarioSelector';
import { usePrintScenarioSelection } from './usePrintScenarioSelection';
import { usePrintGridSelection } from './usePrintGridSelection';
import { useDispatch, useSelector } from 'react-redux';
import * as printPdfActionCreator from '@makemydeal/dr-dash-store/actions/printPdfActionCreator';
import {
    paymentGridDownPaymentCheckboxClicked,
    paymentGridTermCheckboxClicked
} from '@makemydeal/dr-dash-store/actions/proposalActionCreators';
import type { ScopeGridSelections } from '@makemydeal/dr-dash-store/types/types';
import * as printPdfSelectors from '@makemydeal/dr-dash-store/selectors/printPdfSelectors';
import * as proposalSelectors from '@makemydeal/dr-dash-store/selectors/proposalSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import * as quoteSelectors from '@makemydeal/dr-dash-store/selectors/quoteSelectors';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { logNewRelicPageAction, BreakPoint } from '@makemydeal/dr-activities-common';

import { useCustomerUI } from '../customerUI/useCustomerUI';
import { ChatBubbleLeftIcon, EnvelopeIcon, PrinterIcon } from '@interstate/components/Icons';
import { LoadingSection } from '@makemydeal/dr-shared-components';
import type { ToastType } from '../common/DataLoadingToast/Toast';
import { Toast } from '../common/DataLoadingToast/Toast';
import { createEmailPrintingProposalEvent, createTextPrintingProposalEvent } from '../dealTemplates/analytics/cdlEventCreators';

const SHARING_TYPE_TEXT = 'text';
const SHARING_TYPE_EMAIL = 'email';
const SLIDE_OUT_ID = 'print-preview-slide-out';
// SlideOut derives its footer id from the SlideOut id, and only exposes the footer through it.
const FOOTER_SELECTOR = `#ids-SlideOut-footer-${SLIDE_OUT_ID}`;

export interface PrintPreviewSlideOutProps {
    show: boolean;
    onHide?: () => void;
    header: string;
    onBack?: () => void;
    onPrint?: (
        selectedProposalId?: string,
        isFourSquare?: boolean,
        selectedScopeIds?: string[],
        scopeGridSelections?: ScopeGridSelections
    ) => void;
    onText?: () => void;
    onEmail?: () => void;
    onPublish?: (selectedProposalId?: string, selectedScopeIds?: string[], scopeGridSelections?: ScopeGridSelections) => void;
}

export const getErrorMessage = (type: ToastType) => {
    if (type === 'error') {
        return 'Error printing proposal.';
    }
    return undefined;
};

export const PrintPreviewSlideOut: React.FC<PrintPreviewSlideOutProps> = ({
    show,
    onHide,
    header,
    onBack,
    onPrint,
    onText,
    onEmail,
    onPublish
}) => {
    const theme = useInterstateTheme();
    const primaryProposalId = useSelector(proposalSelectors.getPrimaryProposalId);
    const [selectedProposalId, setSelectedProposalId] = useState<string | undefined>(primaryProposalId);
    const isLoadingPreviewError = useSelector(proposalSelectors.getProposalPreviewError);
    const slideOutContentRef = React.useRef<HTMLDivElement>(null);
    const isPrintLoading = useSelector(printPdfSelectors.isLoading);
    const isPrintSuccess = useSelector(printPdfSelectors.isSuccess);
    const isPrintError = useSelector(printPdfSelectors.isError);
    const appliedTemplateId = useSelector(dealTemplateSelectors.getAppliedDealTemplateId);
    const isMvLiteEnabled = useSelector(dealerSelectors.isMvLiteEnabled);
    const isTraditionalMode = useSelector(dealerSelectors.getIsTraditionalMode);
    const isQuoteMode = useSelector(quoteSelectors.getQuoteMode);
    const isEnableCustomerInQuoteModeEnabled = useSelector(featureToggleSelectors.isEnableCustomerInQuoteModeEnabled);
    const commonConsumerId = useSelector(offerReduxSelectors.getShopperCommonConsumerId);
    const proposalList = useSelector(proposalSelectors.getProposalList);
    const isMobileFooter = useMediaQuery({ query: `(max-width: ${BreakPoint.PHONE})` });

    const isSharingDisabled = isQuoteMode || (isEnableCustomerInQuoteModeEnabled && !commonConsumerId);

    const selectedProposal = proposalList.find((p) => p.id === selectedProposalId);
    const isFourSquare = selectedProposal?.type === 'fourSquare';

    const {
        selectableScopeSummaries,
        selectedScopeIds,
        setSelectedScopeIds,
        showScenarioSelector: showSelector,
        hasSelectedScopes,
        getSelectedScopeIdsForAction,
        resetSelection
    } = usePrintScenarioSelection(isFourSquare);

    const { scopeGridInfos, scopeGridSelections, toggleTermIndex, toggleDownPaymentIndex, resetAllSelections } =
        usePrintGridSelection(selectedScopeIds);

    const panelWidth = useGetDrawerWidth({ large: 1200, extraLarge: 1200, tablet: '90vw', small: '100vw', extraSmall: '100vw' });

    useEffect(() => {
        if (isPrintSuccess) {
            onHide?.();
        }
    }, [isPrintSuccess, onHide]);

    useEffect(() => {
        slideOutContentRef.current?.parentElement?.scrollTo(0, 0);
    }, [isLoadingPreviewError]);

    const dispatch = useDispatch();

    // Wrap the payment-grid toggles so we dispatch the analytics-only actions (dc-mv-323/324) without
    // coupling the presentational PrintScenarioSelector/PrintGridControls components to the store.
    const handleToggleTermIndex = useCallback(
        (scopeId: string, index: number) => {
            dispatch(paymentGridTermCheckboxClicked());
            toggleTermIndex(scopeId, index);
        },
        [dispatch, toggleTermIndex]
    );

    const handleToggleDownPaymentIndex = useCallback(
        (scopeId: string, index: number) => {
            dispatch(paymentGridDownPaymentCheckboxClicked());
            toggleDownPaymentIndex(scopeId, index);
        },
        [dispatch, toggleDownPaymentIndex]
    );

    const { showCustomerCard } = useCustomerUI();
    const sharingUrl = useSelector(printPdfSelectors.getSharingUrl);
    const sharingType = useSelector(printPdfSelectors.getSharingType);
    const shopperInfo = useSelector(offerReduxSelectors.getShopperInfo);
    const customerFirstName = shopperInfo.firstName;

    const getCustomMessageWithUrl = useCallback((type: string, url: string, firstName: string) => {
        return () => {
            if (type === SHARING_TYPE_TEXT) {
                return `${firstName}, here's your personalized deal link: ${url}`;
            } else {
                return `${firstName}, here's your personalized <a href="${url}" target="_blank" rel="noopener noreferrer" style="text-decoration: underline;">deal link</a>.`;
            }
        };
    }, []);

    useEffect(() => {
        if (sharingUrl && sharingType) {
            const urlSelector = getCustomMessageWithUrl(sharingType, sharingUrl, customerFirstName);
            const defaultTab = sharingType === SHARING_TYPE_TEXT ? SHARING_TYPE_TEXT : SHARING_TYPE_EMAIL;

            showCustomerCard(true, {
                tab: 'inbox',
                defaultTab,
                messageContentSelector: urlSelector,
                waitForOfferSave: false
            });
            dispatch(printPdfActionCreator.clearPdfUrlForSharing());
        }
    }, [sharingUrl, sharingType, customerFirstName, showCustomerCard, dispatch, getCustomMessageWithUrl]);

    const handleClose = () => {
        onHide?.();
    };

    const handleBack = () => {
        onBack?.();
    };

    const handlePrint = () => {
        logNewRelicPageAction('MV:Printing Proposal - Print clicked', {
            proposalAction: 'print',
            proposalId: selectedProposalId
        });
        onPrint?.(selectedProposalId, isFourSquare, getSelectedScopeIdsForAction(), scopeGridSelections);
    };

    const handleText = () => {
        logNewRelicPageAction('MV:Printing Proposal - Text clicked', {
            proposalAction: 'text',
            proposalId: selectedProposalId
        });

        const analyticsEvent = createTextPrintingProposalEvent(
            {
                templateId: appliedTemplateId
            },
            isMvLiteEnabled
        );
        dispatch(analyticsEvent);
        dispatch(printPdfActionCreator.printPdfForText(selectedProposalId, getSelectedScopeIdsForAction(), scopeGridSelections));
        onText?.();
    };

    const handleEmail = () => {
        logNewRelicPageAction('MV:Printing Proposal - Email clicked', {
            proposalAction: 'email',
            proposalId: selectedProposalId
        });

        const analyticsEvent = createEmailPrintingProposalEvent(
            {
                templateId: appliedTemplateId
            },
            isMvLiteEnabled
        );
        dispatch(analyticsEvent);
        dispatch(printPdfActionCreator.printPdfForEmail(selectedProposalId, getSelectedScopeIdsForAction(), scopeGridSelections));
        onEmail?.();
    };

    const handlePublish = () => {
        logNewRelicPageAction('MV:Printing Proposal - Publish to Sales View clicked', {
            proposalAction: 'salesView',
            proposalId: selectedProposalId
        });
        onPublish?.(selectedProposalId, getSelectedScopeIdsForAction(), scopeGridSelections);
    };

    const handleProposalChange = useCallback(
        (event: { target: { value: string | undefined } }) => {
            setSelectedProposalId(event.target.value as string);
            resetSelection();
            resetAllSelections();
        },
        [resetSelection, resetAllSelections]
    );

    useEffect(() => {
        if (!show) {
            setSelectedProposalId(primaryProposalId);
            resetSelection();
            resetAllSelections();
        }
    }, [show, primaryProposalId, resetSelection, resetAllSelections]);

    return (
        <SlideOut
            id={SLIDE_OUT_ID}
            show={show}
            onHide={handleClose}
            header={header}
            position="right"
            panelWidth={panelWidth}
            data-testid="print-preview-slide-out"
            sx={{
                '.MuiDrawer-paper': {
                    display: 'flex',
                    flexDirection: 'column'
                },
                '.ids-SlideOut-content': {
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 0
                },
                [FOOTER_SELECTOR]: {
                    padding: 0,
                    marginTop: 'auto'
                }
            }}
            footer={{
                footerComponent: (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            flexWrap: 'nowrap',
                            gap: 2,
                            backgroundColor: theme.tokens.ColorSurfaceSubdued,
                            padding: 2
                        }}
                    >
                        {isMobileFooter && isTraditionalMode ? (
                            <>
                                <Button buttonStyle="tertiary" size="sm" onClick={handleBack} data-testid="close-button">
                                    Cancel
                                </Button>
                                <SplitButton
                                    id="publish-to-sales-view-split-button"
                                    data-testid="publish-to-sales-view-split-button"
                                    buttonStyle="primary"
                                    size="sm"
                                    onClick={handlePublish}
                                    disabled={showSelector && !hasSelectedScopes}
                                    options={[
                                        {
                                            id: 'text',
                                            label: 'Text',
                                            value: 'text',
                                            icon: <ChatBubbleLeftIcon />,
                                            onSelect: handleText,
                                            disabled: (showSelector && !hasSelectedScopes) || isSharingDisabled
                                        },
                                        {
                                            id: 'email',
                                            label: 'Email',
                                            value: 'email',
                                            icon: <EnvelopeIcon />,
                                            onSelect: handleEmail,
                                            disabled: (showSelector && !hasSelectedScopes) || isSharingDisabled
                                        },
                                        {
                                            id: 'print',
                                            label: 'Print',
                                            value: 'print',
                                            icon: <PrinterIcon />,
                                            onSelect: handlePrint,
                                            disabled: showSelector && !hasSelectedScopes
                                        }
                                    ]}
                                >
                                    Publish to Sales View
                                </SplitButton>
                            </>
                        ) : (
                            <>
                                <Button buttonStyle="tertiary" onClick={handleBack} data-testid="close-button">
                                    Close
                                </Button>
                                <Button
                                    buttonStyle="secondary"
                                    onClick={handleText}
                                    data-testid="text-button"
                                    startIcon={<ChatBubbleLeftIcon />}
                                    disabled={(showSelector && !hasSelectedScopes) || isSharingDisabled}
                                >
                                    Text
                                </Button>
                                <Button
                                    buttonStyle="secondary"
                                    onClick={handleEmail}
                                    data-testid="email-button"
                                    startIcon={<EnvelopeIcon />}
                                    disabled={(showSelector && !hasSelectedScopes) || isSharingDisabled}
                                >
                                    Email
                                </Button>
                                <Button
                                    buttonStyle={isTraditionalMode ? 'secondary' : 'primary'}
                                    onClick={handlePrint}
                                    data-testid="print-button"
                                    startIcon={<PrinterIcon />}
                                    disabled={showSelector && !hasSelectedScopes}
                                >
                                    Print
                                </Button>
                                {isTraditionalMode && (
                                    <Button
                                        buttonStyle="primary"
                                        onClick={handlePublish}
                                        data-testid="publish-to-sales-view-button"
                                        disabled={showSelector && !hasSelectedScopes}
                                    >
                                        Publish to Sales View
                                    </Button>
                                )}
                            </>
                        )}
                    </Box>
                )
            }}
        >
            <Box sx={{ flexGrow: 1, minHeight: 0, padding: '0 1.5rem', display: 'flex', flexDirection: 'column' }}>
                <LoadingSection loading={isPrintLoading} testId={'loading-pdf-generation-loader'} size={70} />
                <Toast isLoading={isPrintLoading} isLoaded={isPrintSuccess} isError={isPrintError} getMessage={getErrorMessage} />
                {show && (
                    <Box
                        ref={slideOutContentRef}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                            minHeight: 0,
                            overflow: 'auto',
                            gap: '1rem',
                            px: 1
                        }}
                    >
                        <PrintProposalSelector onProposalChange={handleProposalChange} selectedProposalId={selectedProposalId} />
                        {showSelector && (
                            <PrintScenarioSelector
                                scopeSummaries={selectableScopeSummaries}
                                selectedScopeIds={selectedScopeIds}
                                onSelectionChange={setSelectedScopeIds}
                                isFourSquare={isFourSquare}
                                scopeGridInfos={scopeGridInfos}
                                onToggleTermIndex={handleToggleTermIndex}
                                onToggleDownPaymentIndex={handleToggleDownPaymentIndex}
                            />
                        )}
                        <PrintProposalPreview
                            proposalId={selectedProposalId}
                            selectedScopeIds={getSelectedScopeIdsForAction()}
                            scopeGridSelections={scopeGridSelections}
                        />
                    </Box>
                )}
            </Box>
        </SlideOut>
    );
};
